import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { filtersToParams } from '@/components/dashboard/DashboardFilters';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import {
  Phone, Star, Target, TrendingUp, TrendingDown, Award,
  AlertCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Sparkles, Activity, Mic,
} from 'lucide-react';

const TOOLTIP_STYLE = {
  background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, fontSize: 11, color: '#fff',
};

// Niveles de interés y sus colores (paralelos al backend _INTEREST_LEVELS).
const INTEREST_COLORS = {
  'Muy Interesado': '#10B981',   // green
  'Interesado': '#1B9CEA',       // blue
  'Poco Interesado': '#F59E0B',  // amber
  'Nada Interesado': '#F97316',  // orange
  'Cero Interactivo': '#6B7280', // gray
};
const INTEREST_BADGE = {
  'Muy Interesado': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Interesado': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Poco Interesado': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'Nada Interesado': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  'Cero Interactivo': 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
};

// WS-4.2 — Mapa de origen para la lista de llamadas.
const ORIGIN_BADGE = {
  ai_agent:         { label: 'IA',     icon: '🤖', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  nurturing_engine: { label: 'Auto',   icon: '🔄', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  manual:           { label: 'Manual', icon: '👤', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
};

// Detecta si initiated_by es un UUID (= manual de vendedor) y normaliza.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const resolveOriginKey = (initiated_by) => {
  if (!initiated_by) return null;
  if (initiated_by === 'ai_agent') return 'ai_agent';
  if (initiated_by === 'nurturing_engine' || initiated_by === 'auto') return 'nurturing_engine';
  if (initiated_by === 'manual') return 'manual';
  if (typeof initiated_by === 'string' && UUID_RE.test(initiated_by)) return 'manual';
  return null;
};

// WS-4.2 — Estados terminales del status_counts del summary.
const CALL_RESULT_STATUSES = [
  { key: 'completed',         label: 'Conectada',             emoji: '🟢', cls: 'bg-emerald-500/10 border-emerald-500/30', text: 'text-emerald-300' },
  { key: 'vendor_no_answer',  label: 'Vendedor no contestó',  emoji: '🔴', cls: 'bg-rose-500/10 border-rose-500/30',       text: 'text-rose-300' },
  { key: 'vendor_voicemail',  label: 'Buzón del vendedor',    emoji: '📵', cls: 'bg-orange-500/10 border-orange-500/30',   text: 'text-orange-300' },
  { key: 'lead_canceled',     label: 'Lead colgó antes',      emoji: '🟡', cls: 'bg-amber-500/10 border-amber-500/30',     text: 'text-amber-300' },
  { key: 'lead_no_answer',    label: 'Lead no contestó',      emoji: '⚪', cls: 'bg-zinc-500/10 border-zinc-500/30',       text: 'text-zinc-300' },
];

const EMPTY = 'Sin llamadas analizadas en este periodo';

function scoreColor(score) {
  if (score >= 8) return 'text-emerald-400';
  if (score >= 6) return 'text-sky-400';
  if (score >= 4) return 'text-amber-400';
  return 'text-rose-400';
}

function ScoreBar({ score, max = 10 }) {
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  const bar =
    score >= 8 ? 'bg-emerald-500' :
    score >= 6 ? 'bg-sky-500' :
    score >= 4 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function truncate(str, n) {
  if (!str) return '';
  const s = String(str).replace(/\n+/g, ' ').trim();
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function CallForceTab({ filters, vertical = 'generic' }) {
  const [summary, setSummary] = useState(null);
  const [ranking, setRanking] = useState({ top: [], bottom: [], ia: null });
  const [mktVs, setMktVs] = useState([]);
  const [calls, setCalls] = useState({ items: [], total: 0, page: 1, page_size: 20, has_more: false });
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [porQue, setPorQue] = useState({
    llamadas: 0, por_estado: {}, ultimo_paso: {}, sin_bitacora: 0, g4_enabled: false,
  });
  const [loading, setLoading] = useState({ summary: true, ranking: true, mkt: true, calls: true, porQue: true });

  const params = useMemo(() => {
    const p = filtersToParams(filters);
    if (p.seller_id) p.vendor_id = p.seller_id;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading({ summary: true, ranking: true, mkt: true, calls: true, porQue: true });
    const settle = (k) => setLoading(prev => ({ ...prev, [k]: false }));

    api.get('/callforce/summary', { params })
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => settle('summary'));

    api.get('/callforce/vendor-ranking', { params })
      .then(r => setRanking(r.data || { top: [], bottom: [], ia: null }))
      .catch(() => setRanking({ top: [], bottom: [], ia: null }))
      .finally(() => settle('ranking'));

    api.get('/callforce/mkt-vs-ventas', { params })
      .then(r => setMktVs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMktVs([]))
      .finally(() => settle('mkt'));

    api.get('/callforce/calls', { params: { ...params, page, page_size: 20 } })
      .then(r => setCalls(r.data || { items: [], total: 0, page: 1, page_size: 20, has_more: false }))
      .catch(() => setCalls({ items: [], total: 0, page: 1, page_size: 20, has_more: false }))
      .finally(() => settle('calls'));

    const pqParams = {};
    if (params.date_from) pqParams.desde = params.date_from;
    api.get('/callforce/por-que-fallan', { params: pqParams })
      .then(r => setPorQue(r.data || {
        llamadas: 0, por_estado: {}, ultimo_paso: {}, sin_bitacora: 0,
      }))
      .catch(() => setPorQue({
        llamadas: 0, por_estado: {}, ultimo_paso: {}, sin_bitacora: 0,
      }))
      .finally(() => settle('porQue'));
  }, [params, page]);

  useEffect(() => { load(); }, [load]);

  // Reset paginación cuando cambian filtros generales.
  useEffect(() => { setPage(1); }, [filters]);

  const dist = summary?.interest_level_distribution || [];
  const mostFrequent = useMemo(() => {
    if (!dist.length) return null;
    const sorted = [...dist].sort((a, b) => b.value - a.value);
    return sorted[0].value > 0 ? sorted[0].name : null;
  }, [dist]);

  return (
    <div className="space-y-6" data-testid="tab-callforce-content">
      {/* A) KPI CARDS -- fila unica, scroll horizontal en movil (feedback owner jul2026) */}
      <div className="flex overflow-x-auto gap-3 pb-1 sm:grid sm:grid-cols-4">
        <KpiCard
          icon={Star}
          label="Habilidad Ventas Prom."
          value={loading.summary ? '—' : `${summary?.avg_sales_skill_score ?? 0}/10`}
          sub="sales_skill_score"
          color="violet"
          loading={loading.summary}
          testid="cf-kpi-avg-score"
        />
        <KpiCard
          icon={Target}
          label="Tasa de Conversión"
          value={loading.summary ? '—' : `${summary?.conversion_rate ?? 0}%`}
          sub={`${summary?.completed_calls ?? 0}/${summary?.total_recorded_calls ?? 0} grabadas`}
          color="emerald"
          loading={loading.summary}
          testid="cf-kpi-conversion"
        />
        <KpiCard
          icon={Sparkles}
          label="Nivel Más Frecuente"
          value={loading.summary ? '—' : (mostFrequent || 'N/D')}
          sub="moda interest_level"
          color="cyan"
          loading={loading.summary}
          testid="cf-kpi-mode-interest"
        />
        <KpiCard
          icon={Phone}
          label="Llamadas Analizadas"
          value={loading.summary ? '—' : (summary?.total_analyzed_calls ?? 0)}
          sub="analysis_status=completed"
          color="amber"
          loading={loading.summary}
          testid="cf-kpi-total"
        />
      </div>

      {/* G5 — solo si el flag G4 está on/canary para esta empresa */}
      {porQue.g4_enabled === true && (
      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="cf-por-que-fallan">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-white font-medium text-sm">Por qué no completan las llamadas</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Dónde se cae la llamada (no contestó el lead, el vendedor, fallo de red).
                El costo de Twilio y de IA lo ve LeadCEM en Super Admin, no aquí: va incluido en la licencia.
              </p>
            </div>
            <Badge variant="outline" className="text-[10px] border-white/15 shrink-0">
              {loading.porQue ? '…' : `${porQue.llamadas || 0} llamadas`}
            </Badge>
          </div>
          {(porQue.sin_bitacora || 0) > 0 && (
            <p className="text-[11px] text-amber-300/90" data-testid="cf-sin-bitacora">
              {porQue.sin_bitacora} llamadas sin bitácora (anteriores al registro de pasos o sin eventos).
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Por estado final</p>
              <div className="space-y-1" data-testid="cf-por-estado">
                {Object.entries(porQue.por_estado || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs text-white/90">
                    <span className="truncate pr-2">{k}</span>
                    <span className="font-semibold tabular-nums">{v}</span>
                  </div>
                ))}
                {!loading.porQue && Object.keys(porQue.por_estado || {}).length === 0 && (
                  <p className="text-[11px] text-muted-foreground">—</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Último paso (no completadas)</p>
              <div className="space-y-1" data-testid="cf-ultimo-paso">
                {Object.entries(porQue.ultimo_paso || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs text-white/90">
                    <span className="truncate pr-2">{k}</span>
                    <span className="font-semibold tabular-nums">{v}</span>
                  </div>
                ))}
                {!loading.porQue && Object.keys(porQue.ultimo_paso || {}).length === 0 && (
                  <p className="text-[11px] text-muted-foreground">—</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* WS-4.2 — Sección "Resultados de llamadas" (status_counts) */}
      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="cf-status-counts-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-400" />
            <h3 className="text-white font-medium text-sm">Resultados de llamadas</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cf-status-counts-grid">
            {CALL_RESULT_STATUSES.map(({ key, label, emoji, cls, text }) => {
              const n = loading.summary ? null : (summary?.status_counts?.[key] ?? 0);
              const breakdown = summary?.status_counts_by_origin?.[key];
              const hasBreakdown = breakdown && (breakdown.ai > 0 || breakdown.auto > 0 || breakdown.manual > 0);
              return (
                <div
                  key={key}
                  className={`rounded-lg border ${cls} p-3 flex items-center gap-3`}
                  data-testid={`cf-status-count-${key}`}
                >
                  <div className="text-2xl leading-none">{emoji}</div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-2xl font-bold leading-none ${text}`} data-testid={`cf-status-count-value-${key}`}>
                      {loading.summary ? '—' : n}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                      {label}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70">llamadas</p>
                    {hasBreakdown && (
                      <div className="flex gap-2 mt-1" data-testid={`cf-status-count-origin-${key}`}>
                        {breakdown.ai > 0 && (
                          <span className="text-xs opacity-70" data-testid={`cf-status-count-origin-ai-${key}`}>🤖 {breakdown.ai}</span>
                        )}
                        {breakdown.auto > 0 && (
                          <span className="text-xs opacity-70" data-testid={`cf-status-count-origin-auto-${key}`}>🔄 {breakdown.auto}</span>
                        )}
                        {breakdown.manual > 0 && (
                          <span className="text-xs opacity-70" data-testid={`cf-status-count-origin-manual-${key}`}>👤 {breakdown.manual}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* C) DONUT + D) RANKING */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Distribución de nivel de interés"
          icon={Activity}
          loading={loading.summary}
          empty={!dist.some(d => d.value > 0)}
          emptyMessage={EMPTY}
          testid="cf-chart-interest-donut"
          tabKey="callforce"
          chartId="cf-chart-interest-donut"
        >
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={dist}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {dist.map((entry) => (
                  <Cell key={entry.name} fill={INTEREST_COLORS[entry.name] || '#888'} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Ranking de vendedores (score)"
          icon={Award}
          loading={loading.ranking}
          empty={!ranking.top.length && !ranking.ia}
          emptyMessage={EMPTY}
          testid="cf-chart-ranking"
          tabKey="callforce"
          chartId="cf-chart-ranking"
        >
          {ranking.ia && (
            <div className="mb-3 p-2 rounded bg-amber-500/5 border border-amber-500/25" data-testid="cf-ranking-ia">
              <div className="flex items-center justify-between mb-1">
                <span className="text-amber-300 font-semibold">{ranking.ia.name}</span>
                <span className={`font-bold ${scoreColor(ranking.ia.avg_score)}`}>{ranking.ia.avg_score}</span>
              </div>
              <ScoreBar score={ranking.ia.avg_score} />
              <p className="text-[10px] text-muted-foreground mt-1">{ranking.ia.calls} llamadas atendidas por la IA · compara su eficiencia con la de los vendedores</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div data-testid="cf-ranking-top">
              <div className="flex items-center gap-1.5 mb-2 text-emerald-400 font-semibold">
                <TrendingUp className="w-3.5 h-3.5" /> Top {Math.min(5, ranking.top.length)}
              </div>
              {ranking.top.slice(0, 5).map((v) => (
                <div key={v.vendor_id} className="mb-2 p-2 rounded bg-emerald-500/5 border border-emerald-500/15">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white truncate">{v.name}</span>
                    <span className={`font-bold ${scoreColor(v.avg_score)}`}>{v.avg_score}</span>
                  </div>
                  <ScoreBar score={v.avg_score} />
                  <p className="text-[10px] text-muted-foreground mt-1">{v.calls} llamadas</p>
                </div>
              ))}
              {!ranking.top.length && <p className="text-muted-foreground text-[11px]">{EMPTY}</p>}
            </div>
            <div data-testid="cf-ranking-bottom">
              <div className="flex items-center gap-1.5 mb-2 text-rose-400 font-semibold">
                <TrendingDown className="w-3.5 h-3.5" /> Bottom {Math.min(5, ranking.bottom.length)}
              </div>
              {ranking.bottom.slice(0, 5).map((v) => (
                <div key={v.vendor_id} className="mb-2 p-2 rounded bg-rose-500/5 border border-rose-500/15">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white truncate">{v.name}</span>
                    <span className={`font-bold ${scoreColor(v.avg_score)}`}>{v.avg_score}</span>
                  </div>
                  <ScoreBar score={v.avg_score} />
                  <p className="text-[10px] text-muted-foreground mt-1">{v.calls} llamadas</p>
                </div>
              ))}
              {!ranking.bottom.length && (
                <p className="text-muted-foreground text-[11px]">
                  Se necesitan ≥ 11 vendedores con llamadas
                </p>
              )}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* E) MKT VS VENTAS */}
      <ChartCard
        title="Marketing vs Ventas — score por campaña"
        subtitle="Solo campañas con ≥ 3 llamadas analizadas"
        icon={TrendingUp}
        loading={loading.mkt}
        empty={!mktVs.length}
        emptyMessage="Aún no hay campañas con suficientes llamadas analizadas"
        testid="cf-chart-mkt-vs-ventas"
        height={Math.max(280, mktVs.length * 40)}
        tabKey="callforce"
        chartId="cf-chart-mkt-vs-ventas"
      >
        <ResponsiveContainer width="100%" height={Math.max(260, mktVs.length * 40)}>
          <BarChart data={mktVs} layout="vertical" margin={{ left: 80, right: 20, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              type="number"
              domain={[0, 10]}
              tick={{ fill: '#8893A7', fontSize: 10 }}
              axisLine={false}
              label={{ value: 'Score promedio (0-10)', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }}
            />
            <YAxis
              dataKey="campaign_name"
              type="category"
              tick={{ fill: '#8893A7', fontSize: 10 }}
              axisLine={false}
              width={120}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value, _name, ctx) => [
                `${value}/10 (${ctx?.payload?.calls || 0} llamadas)`,
                'Avg score',
              ]}
            />
            <Bar dataKey="avg_score" radius={[0, 4, 4, 0]}>
              {mktVs.map((row, i) => (
                <Cell key={row.campaign_id || i} fill={
                  row.avg_score >= 8 ? '#10B981' :
                  row.avg_score >= 6 ? '#1B9CEA' :
                  row.avg_score >= 4 ? '#F59E0B' : '#EF4444'
                } />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* F) LISTA DE LLAMADAS */}
      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="cf-calls-list-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-cyan-400" />
              <h3 className="text-white font-medium text-sm">Llamadas analizadas</h3>
              <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">
                {calls.total} total
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={page <= 1 || loading.calls}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="cf-calls-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Anterior
              </Button>
              <span className="text-[11px] text-muted-foreground">
                Página {calls.page}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={!calls.has_more || loading.calls}
                onClick={() => setPage(p => p + 1)}
                data-testid="cf-calls-next"
              >
                Siguiente <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {loading.calls ? (
            <p className="text-xs text-muted-foreground p-4 text-center">Cargando…</p>
          ) : !calls.items.length ? (
            <p className="text-xs text-muted-foreground p-6 text-center" data-testid="cf-calls-empty">
              {EMPTY}
            </p>
          ) : (
            <div className="space-y-2" data-testid="cf-calls-list">
              {calls.items.map((c) => {
                const isExp = expandedRow === c.call_id;
                return (
                  <div
                    key={c.call_id}
                    className="rounded-lg border border-white/5 bg-black/20 overflow-hidden"
                    data-testid={`cf-call-row-${c.call_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedRow(isExp ? null : c.call_id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                      data-testid={`cf-call-toggle-${c.call_id}`}
                    >
                      <div className="flex-shrink-0">
                        {isExp
                          ? <ChevronUp className="w-4 h-4 text-cyan-400" />
                          : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-7 gap-2 items-center">
                        <div className="text-[11px] text-muted-foreground">{formatDate(c.created_at)}</div>
                        <div className="text-xs text-white truncate">{c.vendor_name || 'Sin asignar'}</div>
                        <div className="text-xs text-white truncate">{c.lead_name || 'Sin lead'}</div>
                        <div data-testid={`cf-call-origin-${c.call_id}`}>
                          {(() => {
                            const k = resolveOriginKey(c.initiated_by);
                            if (!k) return null;
                            const o = ORIGIN_BADGE[k];
                            return (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${o.cls}`}>
                                <span>{o.icon}</span>
                                <span>{o.label}</span>
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {c.duration_seconds ? `${c.duration_seconds}s` : '—'}
                        </div>
                        <div>
                          {c.interest_level && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${INTEREST_BADGE[c.interest_level] || 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`}
                              data-testid={`cf-call-interest-${c.call_id}`}
                            >
                              {c.interest_level}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-sm ${scoreColor(c.sales_skill_score || 0)}`}>
                            {c.sales_skill_score ?? '—'}
                          </span>
                          <span className="text-[10px] text-muted-foreground">/10</span>
                        </div>
                      </div>
                    </button>
                    <div className="px-3 pb-3 text-[11px] text-muted-foreground">
                      <span className="text-muted-foreground/70">Resumen:</span> {truncate(c.summary, 100)}
                    </div>

                    {isExp && (
                      <div className="border-t border-white/5 p-4 bg-black/30 space-y-3" data-testid={`cf-call-detail-${c.call_id}`}>
                        {c.recording_url && (
                          <div>
                            <div className="flex items-center gap-2 mb-2 text-[11px] text-cyan-300">
                              <Mic className="w-3 h-3" /> Grabación
                            </div>
                            <audio
                              controls
                              preload="metadata"
                              className="w-full"
                              style={{ height: 36, borderRadius: 8 }}
                              src={c.recording_url}
                              data-testid={`cf-call-audio-${c.call_id}`}
                            >
                              Tu navegador no soporta audio.
                            </audio>
                          </div>
                        )}
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Resumen completo</p>
                          <pre className="whitespace-pre-wrap text-xs text-white/80 font-sans">{c.summary || '—'}</pre>
                        </div>
                        {c.next_step && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Próximo paso</p>
                            <p className="text-xs text-white/80">{c.next_step}</p>
                          </div>
                        )}
                        {Array.isArray(c.improvement_suggestions) && c.improvement_suggestions.length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Sugerencias de mejora</p>
                            <ul className="list-disc list-inside text-xs text-white/80 space-y-0.5">
                              {c.improvement_suggestions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {c.metodologia && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Metodología</p>
                              <div className="text-xs text-white/80 space-y-0.5">
                                {c.metodologia.spin && (
                                  <div>SPIN: {Object.entries(c.metodologia.spin).filter(([_, v]) => v).map(([k]) => k).join(', ') || '—'}</div>
                                )}
                                {c.metodologia.aida_etapa && <div>AIDA: {c.metodologia.aida_etapa}</div>}
                                {typeof c.metodologia.cierre_o_cta === 'boolean' && (
                                  <div>Cierre/CTA: {c.metodologia.cierre_o_cta ? 'Sí' : 'No'}</div>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">BANT</p>
                              <div className="text-xs text-white/80 space-y-0.5">
                                {c.qualification_bant && Object.entries(c.qualification_bant).map(([k, v]) => (
                                  <div key={k}><span className="text-muted-foreground capitalize">{k}:</span> {v}</div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {Array.isArray(c.alertas) && c.alertas.length > 0 && (
                          <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20">
                            <div className="flex items-center gap-1.5 text-[11px] text-rose-300 font-semibold mb-1">
                              <AlertCircle className="w-3 h-3" /> Alertas
                            </div>
                            <ul className="list-disc list-inside text-xs text-white/80 space-y-0.5">
                              {c.alertas.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
